import Image from 'next/image'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogAction,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

export type RubrikObservasiPraktikKinerjaDialogProps = Omit<
  DialogProps,
  'children'
> & {
  indikatorTitle: string
  targetKualitas: string
  targetPerilakuList: Array<TargetPerilakuItem>
  isLoading?: boolean
  illustrationPath?: string
}

type TargetPerilakuItem = {
  id: string
  targetPerilaku: string
  perilakuDianjurkanList: string[]
  perilakuDilarangList: string[]
}

export function RubrikObservasiPraktikKinerjaDialog({
  indikatorTitle,
  targetKualitas,
  isLoading = false,
  illustrationPath = '',
  targetPerilakuList,
  isOpen,
  onClose,
  ...dialogProps
}: RubrikObservasiPraktikKinerjaDialogProps) {
  return (
    <Dialog size="lg" {...dialogProps} isOpen={isOpen} autoFocus={false}>
      <DialogOverlay />
      <DialogContent className="!p-0">
        <DialogHeader
          title="Rubrik Observasi Praktik Kinerja"
          subtitle="Berikut Target Perilaku yang akan diobservasi berdasarkan indikator yang dipilih pada tahap Perencanaan."
        />

        <div className="py-6 px-12">
          <div className="flex space-x-4 border border-solid rounded-lg max-w-2xl p-4 mb-6">
            <div className="flex-shrink-0">
              {isLoading ? (
                <Shimmer width={84} height={84} />
              ) : (
                <Image width={84} height={84} src={illustrationPath} alt="" />
              )}
            </div>

            {isLoading ? (
              <IndicatorShimmer />
            ) : (
              <div className="space-y-2">
                <Text variant="action">{indikatorTitle}</Text>
                <Text variant="body" color="subdued">
                  {targetKualitas}
                </Text>
              </div>
            )}
          </div>

          <Text variant="heading-sm" className="mb-4">
            Target Perilaku:
          </Text>

          <div className="space-y-6">
            {isLoading ? (
              <TargetPerilakuShimmer />
            ) : (
              targetPerilakuList?.map((targetPerilaku, index) => (
                <TargetPerilakuItem
                  key={targetPerilaku.id}
                  orderNumber={index + 1}
                  {...targetPerilaku}
                />
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogAction
            as={Button}
            type="button"
            color="blue"
            size="lg"
            onClick={() => onClose()}
          >
            Saya mengerti
          </DialogAction>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type TargetPerilakuItemProps = TargetPerilakuItem & {
  orderNumber: number
}

function TargetPerilakuItem({
  id,
  orderNumber,
  targetPerilaku,
  perilakuDianjurkanList,
  perilakuDilarangList,
}: TargetPerilakuItemProps) {
  return (
    <div key={id}>
      <div className="flex space-x-3 mb-4">
        <div className="bg-blue-70 w-8 h-8 text-white rounded-full text-center align-middle leading-loose flex-shrink-0 min-w-0">
          {orderNumber}
        </div>
        <Text variant="heading-sm" className="self-center !text-black">
          {targetPerilaku}
        </Text>
      </div>
      <div className="flex pl-12 space-x-6">
        <div className="flex flex-col w-full space-y-3">
          <div className="flex space-x-1 items-center">
            <Icon color="success">check_circle_outlined</Icon>
            <Text color="subdued" variant="subheading" className="uppercase">
              Perilaku yang Dianjurkan
            </Text>
          </div>
          <ul className="list-disc ml-7">
            {perilakuDianjurkanList.map(
              (perilakuDianjurkan: string, i: number) => (
                <Text
                  as="li"
                  variant="body"
                  color="subdued"
                  key={perilakuDianjurkan + i}
                >
                  {perilakuDianjurkan}
                </Text>
              )
            )}
          </ul>
        </div>
        <div className="flex flex-col w-full space-y-3">
          <div className="flex space-x-1 ml-0.5 items-center">
            <Icon color="critical">cancel_outlined</Icon>
            <Text color="subdued" variant="subheading" className="uppercase">
              Perilaku yang Dihindari
            </Text>
          </div>
          <ul className="list-disc ml-7">
            {perilakuDilarangList.map((perilakuDilarang: string, i: number) => (
              <Text
                as="li"
                variant="body"
                color="subdued"
                key={perilakuDilarang + i}
              >
                {perilakuDilarang}
              </Text>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function TargetPerilakuShimmer() {
  return (
    <div className="space-y-6">
      {new Array(3).fill('').map((_x, i) => (
        <div className="space-y-4" key={i}>
          <Shimmer className="!w-8/12 !h-6" />

          <div className="flex space-x-6">
            <Shimmer className="!w-full !h-32" />
            <Shimmer className="!w-full !h-32" />
          </div>
        </div>
      ))}
    </div>
  )
}

function IndicatorShimmer() {
  return (
    <div className="space-y-2 w-full">
      <Shimmer className="!w-64" />
      <Shimmer className="!w-full" noOfLines={3} />
    </div>
  )
}
